import { useEffect, useMemo, useState } from 'react';
import { PeriodoDataCalendar } from './PeriodoDataCalendar';

export type StatusProgramadoHistoricoLinha = {
  quando: string;
  dia: string;
  tipo: string;
  modo?: 'periodo' | 'permanente';
  ate?: string;
};

export type StatusProgramadoAgendaPanelProps = {
  minDateYmd: string;
  periodoDe: string;
  periodoAte: string;
  onPeriodoChange: (de: string, ate: string) => void;
  /** Intervalos não clicáveis no calendário (opcional; Estabelecimentos e Experiências não bloqueiam datas já agendadas — períodos sobrepostos coexistem e a hierarquia resolve o tipo efetivo por dia). */
  periodosBloqueados?: { de: string; ate: string }[];
  periodosStatus: { de: string; ate: string; tipo: string }[];
  statusProgramadoHistorico: StatusProgramadoHistoricoLinha[];
  /** Dia civil de referência da agenda (corte 00:10:10) — deve atualizar com o mesmo tick da listagem. */
  diaAgendaStatus: string;
  onRemovePeriodo: (indexInPeriodosStatus: number) => void;
  onAddPeriodo: (tipo: string) => void;
  labelTipoProgramado: (tipo: string) => string;
  /** Tipos de período para classes CSS da tabela (notícia: ativada/desativada; demais: ativado/desativado). */
  tipoAtivo: string;
  tipoDesativado: string;
};

function formatDateBR(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR');
}

export function StatusProgramadoAgendaPanel({
  minDateYmd,
  periodoDe,
  periodoAte,
  onPeriodoChange,
  periodosBloqueados,
  periodosStatus,
  statusProgramadoHistorico,
  diaAgendaStatus,
  onRemovePeriodo,
  onAddPeriodo,
  labelTipoProgramado,
  tipoAtivo,
  tipoDesativado,
}: StatusProgramadoAgendaPanelProps) {
  const [agendaTablePage, setAgendaTablePage] = useState(1);

  const periodosCalendarioAgendaVisual = useMemo(() => {
    const hist = (statusProgramadoHistorico ?? []).map((h) => ({
      de: h.dia,
      ate: h.dia,
      tipo: h.tipo,
    }));
    return [...(periodosStatus ?? []), ...hist];
  }, [periodosStatus, statusProgramadoHistorico]);

  const statusAgendaLinhas = useMemo(() => {
    const periodoConcluido = (p: { de: string; ate: string }) => diaAgendaStatus > p.ate;
    const periodoVigente = (p: { de: string; ate: string }) =>
      !periodoConcluido(p) && diaAgendaStatus >= p.de && diaAgendaStatus <= p.ate;

    const periodos = [...(periodosStatus ?? [])].sort((a, b) => {
      const av = periodoVigente(a);
      const bv = periodoVigente(b);
      if (av !== bv) return av ? -1 : 1;
      if (av && bv) return a.de.localeCompare(b.de);
      const ac = periodoConcluido(a);
      const bc = periodoConcluido(b);
      if (ac !== bc) return ac ? 1 : -1;
      if (!ac && !bc) return a.de.localeCompare(b.de);
      return b.ate.localeCompare(a.ate);
    });
    const linhasPeriodo = periodos.map((p) => ({ tipo: 'periodo' as const, p }));
    const historicos = [...(statusProgramadoHistorico ?? [])]
      .slice()
      .reverse()
      .map((h, revIdx) => ({ tipo: 'hist' as const, h, revIdx }));
    return [...linhasPeriodo, ...historicos];
  }, [periodosStatus, statusProgramadoHistorico, diaAgendaStatus]);

  const agendaRowsPerPage = 8;
  const agendaTotalPages = Math.max(1, Math.ceil(statusAgendaLinhas.length / agendaRowsPerPage));
  const agendaPageSafe = Math.min(Math.max(1, agendaTablePage), agendaTotalPages);
  const statusAgendaPagina = statusAgendaLinhas.slice(
    (agendaPageSafe - 1) * agendaRowsPerPage,
    agendaPageSafe * agendaRowsPerPage
  );

  useEffect(() => {
    setAgendaTablePage((prev) => Math.min(prev, agendaTotalPages));
  }, [agendaTotalPages]);

  const badgeClassForTipo = (tipo: string, periodoConcluido: boolean) => {
    if (periodoConcluido) {
      if (tipo === tipoDesativado) return 'bg-red-50 text-red-700 line-through decoration-red-600/50';
      return 'bg-slate-100 text-slate-700 line-through decoration-slate-500/40';
    }
    if (tipo === tipoDesativado) return 'bg-red-100 text-red-800';
    if (tipo === tipoAtivo) return 'bg-emerald-100 text-emerald-800';
    if (tipo === 'naviex') return 'bg-violet-100 text-violet-800';
    if (tipo === 'principal') return 'bg-amber-100 text-amber-900';
    return 'bg-orange-100 text-orange-900';
  };

  return (
    <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
      <h2 className="text-lg font-semibold text-gray-800 tracking-wide mb-4">Status Programado</h2>

      <div
        className="rounded-2xl border-2 border-gray-200/90 bg-white px-3 py-4 sm:px-5 sm:py-5 shadow-sm w-full"
        role="region"
        aria-label="Agendamento de status programado"
      >
        <div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-0">
          <div className="w-full lg:w-1/2 lg:min-w-0 lg:pr-5 lg:border-r lg:border-gray-200 flex flex-col gap-3">
            <label className="block text-sm font-medium text-gray-700">Período (Data)</label>
            <div className="flex flex-row gap-3 sm:gap-4 items-start min-w-0">
              <div className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                <PeriodoDataCalendar
                  minDate={minDateYmd}
                  periodoDe={periodoDe}
                  periodoAte={periodoAte}
                  periodosBloqueados={periodosBloqueados}
                  periodosAgendaVisual={periodosCalendarioAgendaVisual}
                  diaReferenciaAgendaVisual={diaAgendaStatus}
                  onChange={onPeriodoChange}
                />
              </div>
              <div className="flex flex-col gap-2 shrink-0 w-[10.5rem] sm:w-[11.5rem] pt-0.5">
                <button
                  type="button"
                  onClick={() => onAddPeriodo(tipoAtivo)}
                  className="min-h-[2.75rem] w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-emerald-400 text-emerald-900 bg-emerald-50 hover:bg-emerald-100 transition-colors shadow-sm"
                >
                  Ativar
                </button>
                <button
                  type="button"
                  onClick={() => onAddPeriodo('naviex')}
                  className="min-h-[2.75rem] w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-violet-300 text-violet-900 bg-violet-50 hover:bg-violet-100 transition-colors shadow-sm"
                >
                  Destaque Naviex
                </button>
                <button
                  type="button"
                  onClick={() => onAddPeriodo('principal')}
                  className="min-h-[2.75rem] w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 transition-colors shadow-sm"
                >
                  Destaque Principal
                </button>
                <button
                  type="button"
                  onClick={() => onAddPeriodo('categoria')}
                  className="min-h-[2.75rem] w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-orange-300 text-orange-900 bg-orange-50 hover:bg-orange-100 transition-colors shadow-sm"
                >
                  Destaque Categoria
                </button>
                <button
                  type="button"
                  onClick={() => onAddPeriodo(tipoDesativado)}
                  className="min-h-[2.75rem] w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-red-300 text-red-800 bg-red-50 hover:bg-red-100 transition-colors shadow-sm"
                >
                  Desativado
                </button>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2 lg:min-w-0 lg:pl-5 flex flex-col min-h-0">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Agendamentos e histórico</h3>
            {statusAgendaLinhas.length === 0 ? (
              <p className="text-sm text-gray-500 py-3">Nenhum agendamento ou histórico ainda.</p>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm flex-1 min-h-[12rem] max-h-[min(24rem,55vh)] overflow-y-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="sticky top-0 z-[1] bg-gray-100 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="py-3 px-3 sm:px-4">Tipo</th>
                        <th className="py-3 px-3 sm:px-4">Período</th>
                        <th className="py-3 px-3 sm:px-4">Status</th>
                        <th className="py-3 px-3 sm:px-4 text-right w-24">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {statusAgendaPagina.map((linha) => {
                        if (linha.tipo === 'periodo') {
                          const p = linha.p;
                          const periodoConcluido = diaAgendaStatus > p.ate;
                          const periodoVigente =
                            !periodoConcluido && diaAgendaStatus >= p.de && diaAgendaStatus <= p.ate;
                          const idxOriginal = (periodosStatus ?? []).findIndex(
                            (x) => x.de === p.de && x.ate === p.ate && x.tipo === p.tipo
                          );
                          return (
                            <tr
                              key={`periodo-${p.de}-${p.ate}-${p.tipo}`}
                              className={
                                periodoConcluido
                                  ? 'bg-slate-50/90 text-slate-600'
                                  : periodoVigente
                                    ? 'bg-white border-l-4 border-l-emerald-500'
                                    : 'bg-white border-l-4 border-l-orange-400'
                              }
                            >
                              <td className="py-3 px-3 sm:px-4 align-top whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${
                                    periodoConcluido
                                      ? 'bg-slate-200 text-slate-700'
                                      : periodoVigente
                                        ? 'bg-emerald-100 text-emerald-900'
                                        : 'bg-orange-100 text-orange-900'
                                  }`}
                                >
                                  {periodoConcluido ? 'Concluído' : periodoVigente ? 'Vigente' : 'Agendamento'}
                                </span>
                              </td>
                              <td className="py-3 px-3 sm:px-4 align-top text-gray-800">
                                {p.de === p.ate ? (
                                  <>Dia {formatDateBR(`${p.de}T12:00:00`)}</>
                                ) : (
                                  <>
                                    {formatDateBR(`${p.de}T12:00:00`)} — {formatDateBR(`${p.ate}T12:00:00`)}
                                  </>
                                )}
                              </td>
                              <td className="py-3 px-3 sm:px-4 align-top">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${badgeClassForTipo(
                                    p.tipo,
                                    periodoConcluido
                                  )}`}
                                >
                                  {labelTipoProgramado(p.tipo)}
                                </span>
                              </td>
                              <td className="py-3 px-3 sm:px-4 align-top text-right">
                                {!periodoConcluido && idxOriginal >= 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => onRemovePeriodo(idxOriginal)}
                                    className="inline-flex p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50"
                                    title="Excluir agendamento"
                                    aria-label="Excluir agendamento"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400 tabular-nums">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        }
                        const { h, revIdx } = linha;
                        return (
                          <tr
                            key={`hist-${h.quando}-${h.dia}-${h.tipo}-${h.modo ?? ''}-${revIdx}`}
                            className="bg-gray-50/95 text-gray-600 border-l-4 border-l-gray-300"
                          >
                            <td className="py-3 px-3 sm:px-4 align-top whitespace-nowrap">
                              <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700">
                                Histórico
                              </span>
                            </td>
                            <td className="py-3 px-3 sm:px-4 align-top">
                              {h.modo === 'permanente' ? (
                                <>
                                  Ajuste permanente: <strong>{labelTipoProgramado(h.tipo)}</strong> (referência: dia{' '}
                                  {formatDateBR(`${h.dia}T12:00:00`)})
                                </>
                              ) : h.modo === 'periodo' && h.ate ? (
                                <>
                                  Período agendado {formatDateBR(`${h.dia}T12:00:00`)} — {formatDateBR(`${h.ate}T12:00:00`)}
                                </>
                              ) : (
                                <>Registro — dia {formatDateBR(`${h.dia}T12:00:00`)}</>
                              )}
                              <div className="mt-1 text-xs text-gray-500 tabular-nums">
                                {formatDateBR(h.quando)} às{' '}
                                {new Date(h.quando).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </td>
                            <td className="py-3 px-3 sm:px-4 align-top">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-200/80 text-gray-700">
                                {labelTipoProgramado(h.tipo)}
                              </span>
                            </td>
                            <td className="py-3 px-3 sm:px-4 align-top text-right text-xs text-gray-400">—</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {agendaTotalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-gray-700">
                    <button
                      type="button"
                      disabled={agendaPageSafe <= 1}
                      onClick={() => setAgendaTablePage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      Anterior
                    </button>
                    <span className="tabular-nums text-gray-600">
                      Página {agendaPageSafe} de {agendaTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={agendaPageSafe >= agendaTotalPages}
                      onClick={() => setAgendaTablePage((p) => Math.min(agendaTotalPages, p + 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
